<template>
  <div class="trainer-page">
    <v-card class="trainer-card">
      <div class="trainer-header">
        <div class="trainer-header__copy">
          <div class="trainer-title">Trainers</div>
          <div class="trainer-subtitle">Manage trainer profiles and course assignments.</div>
        </div>
        <div class="trainer-header__actions">
          <v-btn v-if="canAssignTrainer" class="trainer-add-button" depressed @click="openTrainerAssignment">
            <v-icon left>mdi-account-plus</v-icon>
            <span>Assign Trainer</span>
          </v-btn>
          <v-btn
              v-if="canCreateTrainer"
              class="trainer-add-button"
              depressed
              @click="addTrainer()"
          >
            <v-icon left>mdi-plus-circle</v-icon>
            <span>Add Trainer</span>
          </v-btn>
        </div>
      </div>
      <div v-if="assignmentSearchOpen && canAssignTrainer" class="trainer-assignment-toolbar">
        <div class="trainer-assignment-toolbar__title">Assign Trainer</div>
        <v-autocomplete
            ref="assignmentSearch"
            v-model="selectedAssignmentTrainer"
            :items="assignmentTrainers"
            :loading="assignmentSearchLoading"
            :search-input.sync="assignmentSearch"
            :item-text="assignmentTrainerLabel"
            item-value="id"
            label="Search trainers across all CPD providers"
            placeholder="Search by name, phone or email"
            :no-data-text="(assignmentSearch || '').trim().length < 3 ? 'Type at least 3 characters to search' : 'No trainers found'"
            prepend-inner-icon="mdi-database-search"
            clearable outlined dense hide-details no-filter return-object
            @change="selectTrainerForAssignment"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ trainerFullName(item) }}</v-list-item-title>
              <v-list-item-subtitle>{{ [item.phoneNumber, item.email].filter(Boolean).join(' · ') }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
        <v-btn icon aria-label="Close trainer search" @click="hideTrainerAssignmentSearch">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="trainer-filters">
        <v-menu v-model="startMenu" :close-on-content-click="false" offset-y min-width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="filters.startDate" label="Event start date" prepend-inner-icon="mdi-calendar"
                          clearable readonly dense hide-details v-bind="attrs" v-on="on" />
          </template>
          <v-date-picker v-model="filters.startDate" :max="filters.endDate || null" no-title
                         @input="startMenu = false" />
        </v-menu>
        <v-menu v-model="endMenu" :close-on-content-click="false" offset-y min-width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="filters.endDate" label="Event end date" prepend-inner-icon="mdi-calendar"
                          clearable readonly dense hide-details v-bind="attrs" v-on="on" />
          </template>
          <v-date-picker v-model="filters.endDate" :min="filters.startDate || null" no-title
                         @input="endMenu = false" />
        </v-menu>
        <v-autocomplete v-model="filters.categoryId" :items="filteredCategories" :loading="filterLookupsLoading"
                        item-text="name" item-value="id" label="CPD activity category" clearable dense hide-details />
        <v-autocomplete v-model="filters.cpdActivityId" :items="filteredCpdActivities" :loading="filterLookupsLoading"
                        :item-text="activityLabel" item-value="id" label="CPD activity" clearable dense hide-details />
        <v-select v-model="filters.courseStatus" :items="courseStatusOptions" item-text="text" item-value="value"
                  label="Status" clearable dense hide-details />
        <v-select v-model="filters.categoryType" :items="categoryTypeOptions" item-text="text" item-value="value"
                  label="Category type" clearable dense hide-details />
        <v-text-field v-model="search" label="Search name, phone or email" append-icon="mdi-magnify"
                      clearable dense hide-details />
      </div>
      <v-data-table
          :headers="headers"
          :items="trainers.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="trainers.count"
          class="trainer-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.fullName`]="{ item }">
          {{ item.fullName ? item.fullName : "-" }}
        </template>
        <template v-slot:[`item.gender`]="{ item }">
          {{ item.gender ? item.gender : "-" }}
        </template>
        <template v-slot:[`item.email`]="{ item }">
          {{ item.email ? item.email : "-" }}
        </template>
        <template v-slot:[`item.profession.name`]="{ item }">
          {{ item.profession ? item.profession.name : "-" }}
        </template>
        <template v-slot:[`item.otherProfessionName`]="{ item }">
          {{ item.otherProfessionName ? item.otherProfessionName : "-" }}
        </template>
        <template v-if="canViewTrainer" v-slot:[`item.actions`]="{ item }">
          <div class="trainer-action-group">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                    class="trainer-action-icon"
                    @click="viewTrainer(item)"
                    v-on="on"
                >mdi-eye
                </v-icon
                >
              </template>
              <span>View Trainer</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-if="canEditTrainer && item.canEditTrainer !== false" v-slot:activator="{ on }">
                <v-icon
                    class="trainer-action-icon"
                    @click="editTrainer(item)"
                    v-on="on"
                >mdi-circle-edit-outline
                </v-icon
                >
              </template>
              <span>Edit Trainer</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="assignmentDialog" persistent max-width="960px" scrollable>
      <v-card class="trainer-dialog-card">
        <v-card-title class="trainer-dialog__title">
          <v-icon color="white">mdi-account-plus</v-icon>
          <span class="trainer-assignment-dialog__title">Assign CPD Activity</span>
          <v-btn class="trainer-dialog__close" icon :disabled="assignmentSaving" aria-label="Close trainer assignment"
                 @click="closeTrainerAssignment">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="selectedAssignmentTrainer" class="pa-0">
          <div class="trainer-assignment-summary">
            <div class="trainer-assignment-summary__title">Trainer Information</div>
            <dl class="trainer-assignment-summary__grid">
              <div><dt>Full name</dt><dd>{{ trainerFullName(selectedAssignmentTrainer) }}</dd></div>
              <div><dt>Gender</dt><dd>{{ selectedAssignmentTrainer.gender || '—' }}</dd></div>
              <div><dt>Phone number</dt><dd>{{ selectedAssignmentTrainer.phoneNumber || '—' }}</dd></div>
              <div><dt>Email</dt><dd>{{ selectedAssignmentTrainer.email || '—' }}</dd></div>
              <div><dt>Profession</dt><dd>{{ selectedAssignmentTrainer.profession?.name || selectedAssignmentTrainer.otherProfessionName || '—' }}</dd></div>
              <div><dt>Educational level</dt><dd>{{ selectedAssignmentTrainer.educationalLevel?.name || '—' }}</dd></div>
            </dl>
          </div>
          <v-divider />
          <AssignTrainerCourse :key="selectedAssignmentTrainer.id" :saving="assignmentSaving"
                               @saveAssignedCourse="saveTrainerAssignment" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <template>
      <v-dialog v-model="dialog" persistent width="60%">
        <v-card class="trainer-dialog-card">
          <v-card-title
              class="trainer-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="trainer-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdAddTrainer
              v-if="dialog"
              :trainer="trainer"
              @close="closeDialog()"
              @registerOrUpdateTrainer="registerOrUpdateTrainer"
          ></HrdAddTrainer>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="trainer-dialog-card">
          <v-card-title
              class="trainer-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="trainer-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdTrainerDetail
              v-if="detailDialog"
              :trainer="trainer"
          ></HrdTrainerDetail>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>


<script>
import HrdAddTrainer from "./hrd-add-trainer";
import HrdTrainerDetail from "./hrd-trainer-detail";
import AssignTrainerCourse from "./assign-trainer-course.vue";
import {TrainerService} from "./trainers.service";
import {getUser, getCategories, getProgramsLite} from "@/utils/helper";

export default {
  components: {
    HrdAddTrainer,
    HrdTrainerDetail,
    AssignTrainerCourse,
  },

  data() {
    return {
      dialog: false,
      detailDialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      search: "",
      searchDebounce: null,
      assignmentSearchOpen: false,
      assignmentSearch: "",
      assignmentTrainers: [],
      selectedAssignmentTrainer: null,
      assignmentDialog: false,
      assignmentSearchLoading: false,
      assignmentSaving: false,
      assignmentSearchDebounce: null,
      assignmentRequestId: 0,
      filters: {startDate: null, endDate: null, categoryId: null, cpdActivityId: null, courseStatus: null, categoryType: null},
      startMenu: false,
      endMenu: false,
      filterLookupsLoading: false,
      CATEGORIES: [],
      CPD_ACTIVITIES: [],
      listRequestId: 0,
      courseStatusOptions: [
        {text: "Not completed", value: "UNCOMPLETED"},
        {text: "Completed", value: "COMPLETED"},
        {text: "CEO approved", value: "APPROVED"},
      ],
      categoryTypeOptions: [{text: "CAT I", value: "CAT1"}, {text: "CAT II", value: "CAT2"}],
      trainers: {rows: [], count: 0},
      headers: [
        {text: "Trainer Full Name", value: "fullName"},
        {text: "Gender", value: "gender"},
        {text: "Email", value: "email"},
        {text: "Profession", value: "profession.name"},
        {text: "Other Profession Name", value: "otherProfessionName"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      loading: false,
      trainer: null,
    };
  },
  async beforeMount() {
    this.fetchTrainerList();
    this.loadTrainerFilterLookups();
  },
  beforeDestroy() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    if (this.assignmentSearchDebounce) clearTimeout(this.assignmentSearchDebounce);
    this.assignmentRequestId += 1;
    this.listRequestId += 1;
  },
  watch: {
    assignmentSearch(value) {
      this.queueTrainerAssignmentSearch(value);
    },
    options: {
      handler() {
        this.fetchTrainerList();
      },
      deep: true,
    },
    filters: {
      deep: true,
      handler() {
        if (this.filters.categoryId && !this.filteredCategories.some(category => Number(category.id) === Number(this.filters.categoryId))) {
          this.filters.categoryId = null;
        }
        if (this.filters.cpdActivityId && !this.filteredCpdActivities.some(activity => Number(activity.id) === Number(this.filters.cpdActivityId))) {
          this.filters.cpdActivityId = null;
        }
        this.refreshTrainerList();
      },
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.refreshTrainerList();
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.refreshTrainerList();
      }, 400);
    },
  },
  computed: {
    canAssignTrainer() {
      return this.canCreateTrainer || this.canEditTrainer;
    },
    filteredCategories() {
      return this.CATEGORIES.filter(category => !this.filters.categoryType || category.categoryType === this.filters.categoryType);
    },
    filteredCpdActivities() {
      return this.CPD_ACTIVITIES.filter(activity =>
          (!this.filters.categoryId || Number(activity.categoryId) === Number(this.filters.categoryId)) &&
          (!this.filters.categoryType || activity.category?.categoryType === this.filters.categoryType));
    },
    canCreateTrainer() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Trainer" && permission.canCreate
      );
    },
    canEditTrainer() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Trainer" && permission.canUpdate
      );
    },
    canViewTrainer() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Trainer" && permission.canRead
      );
    },
  },
  methods: {
    trainerFullName(trainer) {
      return trainer.fullName || [trainer.trainerName, trainer.trainerFatherName, trainer.trainerGrandfatherName]
          .filter(Boolean).join(" ");
    },
    assignmentTrainerLabel(trainer) {
      return [this.trainerFullName(trainer), trainer.phoneNumber, trainer.email].filter(Boolean).join(" · ");
    },
    openTrainerAssignment() {
      this.assignmentSearchOpen = true;
      this.$nextTick(() => this.$refs.assignmentSearch?.focus());
    },
    hideTrainerAssignmentSearch() {
      this.closeTrainerAssignment();
      this.assignmentSearchOpen = false;
    },
    queueTrainerAssignmentSearch(value) {
      if (this.assignmentSearchDebounce) clearTimeout(this.assignmentSearchDebounce);
      const requestId = ++this.assignmentRequestId;
      const text = (value || "").trim();
      this.assignmentSearchLoading = false;
      if (this.selectedAssignmentTrainer || text.length < 3) {
        this.assignmentTrainers = [];
        return;
      }
      this.assignmentTrainers = [];
      this.assignmentSearchDebounce = setTimeout(() => {
        this.fetchTrainerAssignmentResults(text, requestId);
      }, 400);
    },
    async fetchTrainerAssignmentResults(text, requestId) {
      this.assignmentSearchLoading = true;
      try {
        const response = await TrainerService.searchTrainersForAssignment(text);
        if (requestId !== this.assignmentRequestId) return;
        if (response.status !== 200) throw new Error(response.message || "Unable to search trainers.");
        this.assignmentTrainers = response.data?.rows || [];
      } catch (error) {
        if (requestId === this.assignmentRequestId) {
          this.assignmentTrainers = [];
          this.$toast.error(error.response?.data?.message || error.message || "Unable to search trainers.");
        }
      } finally {
        if (requestId === this.assignmentRequestId) this.assignmentSearchLoading = false;
      }
    },
    selectTrainerForAssignment(trainer) {
      if (!trainer) return;
      this.assignmentRequestId += 1;
      if (this.assignmentSearchDebounce) clearTimeout(this.assignmentSearchDebounce);
      this.assignmentSearchLoading = false;
      this.selectedAssignmentTrainer = trainer;
      this.assignmentDialog = true;
    },
    closeTrainerAssignment() {
      if (this.assignmentSaving) return;
      this.assignmentRequestId += 1;
      if (this.assignmentSearchDebounce) clearTimeout(this.assignmentSearchDebounce);
      this.assignmentDialog = false;
      this.selectedAssignmentTrainer = null;
      this.assignmentSearch = "";
      this.assignmentTrainers = [];
      this.assignmentSearchLoading = false;
    },
    async saveTrainerAssignment(assignment) {
      if (!this.selectedAssignmentTrainer || this.assignmentSaving) return;
      this.assignmentSaving = true;
      let saved = false;
      try {
        const response = await TrainerService.update(this.selectedAssignmentTrainer.id, {...assignment, assigningCourse: true});
        if (response.data?.status !== 200) throw new Error(response.data?.message || "Unable to assign trainer.");
        this.$toast.success(response.data.message || "Trainer assigned successfully.");
        saved = true;
      } catch (error) {
        this.$toast.error(error.response?.data?.message || error.message || "Unable to assign trainer.");
      } finally {
        this.assignmentSaving = false;
      }
      if (saved) {
        this.closeTrainerAssignment();
        this.refreshTrainerList();
      }
    },
    activityLabel(activity) {
      const title = activity.courseTitleCat2 || activity.program_courses?.map(item => item.course?.courseName).filter(Boolean).join(", ") || activity.category?.name || `Activity ${activity.id}`;
      return `${title} (${String(activity.startDate || "").slice(0, 10)} - ${String(activity.endDate || "").slice(0, 10)})`;
    },
    async loadTrainerFilterLookups() {
      this.filterLookupsLoading = true;
      try {
        const [categories, programs] = await Promise.all([getCategories(), getProgramsLite()]);
        this.CATEGORIES = categories || [];
        this.CPD_ACTIVITIES = programs?.data?.rows || [];
      } finally {
        this.filterLookupsLoading = false;
      }
    },
    refreshTrainerList() {
      if (this.options.page !== 1) {
        this.options.page = 1;
        return;
      }
      this.fetchTrainerList();
    },
    async fetchTrainerList() {
      const requestId = ++this.listRequestId;
      this.loading = true;
      try {
        const {page, itemsPerPage} = this.options;
        const res = await TrainerService.getTrainersWP(page, itemsPerPage, this.search, {...this.filters});
        if (requestId !== this.listRequestId) return;
        if (res.status === 200) {
          this.trainers = res.data || {rows: [], count: 0};
        } else {
          this.$toast.error(res.message || "There was a problem fetching trainers. Please try again.");
        }
      } catch (error) {
        if (requestId === this.listRequestId) {
          this.$toast.error(error.response?.data?.message || "There was a problem fetching trainers. Please try again.");
        }
      } finally {
        if (requestId === this.listRequestId) this.loading = false;
      }
    },
    viewTrainer(item) {
      this.trainer = item;
      this.detailDialog = true;
      this.title = "Trainer Detail";
    },
    addTrainer() {
      this.trainer = null;
      this.dialog = true;
      this.title = "Add Trainer";
    },
    editTrainer(item) {
      if (item?.canEditTrainer === false) {
        this.$toast.error("You can only edit trainers registered by your CPD provider.");
        return;
      }
      this.trainer = item;
      this.dialog = true;
      this.title = "Edit Trainer";
    },
    closeDialog() {
      this.dialog = false;
      this.detailDialog = false;
      this.fetchTrainerList();
    },
    async registerOrUpdateTrainer(trainer) {
      let res = null;

      if (trainer.editMode) {
        res = await TrainerService.update(trainer.id, trainer);

        if (res.status === 201 || res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.closeDialog();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to update trainer, please try again");
        }
      } else {
        res = await TrainerService.create(trainer);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.closeDialog();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to create trainer, please try again");
        }
      }
    },
  },
};
</script>
<style scoped>
.trainer-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.trainer-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.trainer-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.trainer-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.trainer-header__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.trainer-assignment-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0 28px 24px;
  padding: 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.trainer-assignment-toolbar__title,
.trainer-assignment-summary__title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.trainer-assignment-summary {
  padding: 24px;
  background: #f8fafc;
}

.trainer-assignment-dialog__title {
  word-break: normal;
  flex: 1;
  min-width: 0;
  line-height: 1.4;
}

.trainer-assignment-summary__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 16px;
}

.trainer-assignment-summary dt {
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.trainer-assignment-summary__grid > div {
  min-width: 0;
}

.trainer-assignment-summary dd {
  margin: 0;
  color: #0f172a;
  overflow-wrap: anywhere;
}

.trainer-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.trainer-subtitle {
  font-size: 14px;
  color: #475569;
}

.trainer-filters {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: #f8fafc;
}

.trainer-filters > * {
  min-width: 0;
}

.trainer-filters ::v-deep .v-input {
  min-width: 0;
  font-size: 13px;
}

.trainer-filters ::v-deep .v-label {
  font-size: 12px;
}

.trainer-filters ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  min-width: 0;
}

.trainer-filters ::v-deep .v-select__selection {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trainer-filters ::v-deep .v-icon {
  font-size: 20px;
}

.trainer-filters ::v-deep .v-input__icon {
  min-width: 20px;
  width: 20px;
}

@media (min-width: 960px) {
  .trainer-filters {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.15fr) minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 1.6fr);
  }
}

.trainer-table {
  border-radius: 0 0 12px 12px;
}

.trainer-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.trainer-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.trainer-dialog__close {
  color: #ffffff !important;
}

.trainer-dialog__close .v-icon {
  color: #ffffff !important;
}

.trainer-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.trainer-action-icon {
  color: #1976d2 !important;
}

.trainer-action-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.trainer-action-group .v-tooltip {
  display: inline-flex;
}

@media (max-width: 700px) {
  .trainer-page {
    padding: 12px;
  }

  .trainer-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .trainer-filters {
    grid-template-columns: 1fr;
    padding: 0 20px 20px;
  }

  .trainer-assignment-toolbar {
    flex-wrap: wrap;
    margin: 0 20px 20px;
  }

  .trainer-assignment-toolbar__title {
    width: 100%;
  }

  .trainer-assignment-summary__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .trainer-dialog__title {
    gap: 8px;
    padding: 16px;
  }

  .trainer-header__actions {
    width: 100%;
  }

  .trainer-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
